import React from 'react';
import { isEqual } from 'lodash-es';
import { subscribe, useSnapshot } from 'valtio';

import { gql } from '@soundxyz/gql-string';
import { getManyFromList } from '@soundxyz/utils';
import { ENVIRONMENT } from '@soundxyz/utils/const';
import { deepTrim } from '@soundxyz/utils/string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useSingleLocationSelect } from '@components/Location/select';
import { useAuth } from '@context/auth/base';
import { ToastContext } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ArtistProfileNewDocument,
  AuthUserDocument,
  AuthUserEditProfileDataNewDocument,
  fetchGQL,
  GetUserProfileByIdDocument,
  GetUserProfileNewDocument,
  MediaType,
  SetArtistSpotifyUrlDocument,
  SetDelegateAddressDocument,
  SetDescriptionDocument,
  SetInvitedByUserDocument,
  SetUserAvatarImageDocument,
  SetUserDisplayNameDocument,
  SetUserLocationDocument,
  UploadArtistBannerImageDocument,
  UpsertUserBannerImageDocument,
  useQuery,
} from '@graphql/reactQuery';
import { useAuthArtist } from '@hooks/useAuthArtist';
import { useLogError } from '@hooks/useLogError';
import { PersistenceStorage } from '@store/persistence';

import {
  clearErrorField,
  clearErrors,
  clearFields,
  setError,
  setField,
  setLoadingField,
} from './helpers';
import { ProfileFieldSchema } from './schema';
import { initialProfileFormState, profileFormState } from './store';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AuthUserEditProfileDataNew {
    authUser {
      id
      displayName
      avatar {
        id
        key
      }
      bannerImage {
        id
        key
      }
      description
      delegateWalletAddress
      invitedByUser {
        id
      }
      artist {
        id
        spotifyUrl
        bannerImage {
          id
          key
        }
      }
    }
  }
  mutation setUserAvatarImage($image: UploadedMedia!) {
    uploadUserMedia(content: $image) {
      id
      avatar {
        id
        url
        key
      }
    }
  }

  mutation setUserDisplayName($displayName: String!) {
    setUserDisplayName(displayName: $displayName) {
      __typename
      ... on MutationSetUserDisplayNameSuccess {
        data {
          id
          displayName
        }
      }

      ... on ValidationError {
        message
      }
    }
  }

  mutation setDelegateAddress($delegateAddress: Address) {
    setDelegateAddress(delegateAddress: $delegateAddress) {
      id
    }
  }

  mutation setDescription($description: String!) {
    updateDescription(description: $description) {
      id
      description
    }
  }

  mutation setArtistSpotifyUrl($spotifyUrl: URL) {
    updateSpotifyArtistUrl(spotifyUrl: $spotifyUrl) {
      __typename
      ... on MutationUpdateSpotifyArtistUrlSuccess {
        data {
          id
          spotifyUrl
        }
      }
      ... on Error {
        message
      }
    }
  }

  mutation setUserLocation($location: GeoLocationInput) {
    setUserLocation(location: $location) {
      ... on MutationSetUserLocationSuccess {
        data {
          id
          location {
            label
            placeId
          }
        }
      }
      ... on Error {
        message
      }
    }
  }

  mutation setInvitedByUser($invitedBy: UUID) {
    setInvitedByUser(invitedBy: $invitedBy) {
      ... on MutationSetInvitedByUserSuccess {
        data {
          id
        }
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [
    SetUserAvatarImageDocument,
    UploadArtistBannerImageDocument,
    UpsertUserBannerImageDocument,
    SetUserDisplayNameDocument,
    SetDelegateAddressDocument,
    SetDescriptionDocument,
    SetArtistSpotifyUrlDocument,
    SetUserLocationDocument,
    SetInvitedByUserDocument,
  ],
  refetch: [
    AuthUserDocument,
    AuthUserEditProfileDataNewDocument,
    ArtistProfileNewDocument,
    GetUserProfileNewDocument,
    GetUserProfileByIdDocument,
  ],
});

// !!!Make sure to version bump whenever you change the schema!!!
const version = '0.2';
const storageKey = `@soundxyz/edit-profile-info${
  ENVIRONMENT === 'production' ? '' : '-' + ENVIRONMENT
}-${version}`;
export const EditProfileFormPersistence = PersistenceStorage({
  schema: ProfileFieldSchema,
  key: storageKey,
});

if (typeof window !== 'undefined') {
  EditProfileFormPersistence.initialValue
    .then(data => {
      if (!data) return;
      Object.assign(profileFormState.fields, data);
    })
    // eslint-disable-next-line no-console
    .catch(console.error)
    .finally(() => {
      subscribe(
        profileFormState.fields,
        () => {
          if (isEqual(profileFormState.fields, initialProfileFormState().fields)) {
            return;
          }

          // Debounce storage updates to minimize CPU Usage on frequent state updates
          const pendingStorageSet = setTimeout(() => {
            const parseToStore = ProfileFieldSchema.safeParse(profileFormState.fields);
            // This way we ensure that we are inserting valid data into the storage
            // reduces sentry noise too
            if (parseToStore.success) {
              EditProfileFormPersistence.set(parseToStore.data);
            }
            clearTimeout(pendingStorageSet);
          }, 0);
        },
        true,
      );
    });
}

export const useEditProfile = () => {
  const { authUser } = useAuth();
  const { authArtist } = useAuthArtist();
  const [isSaving, setIsSaving] = React.useState(false);
  const logError = useLogError();
  const isArtist = !!authArtist;
  const { openToast } = ToastContext.useContainer();

  const {
    fields: snapshotFields,
    errors,
    loading: formLoading,
  } = useSnapshot(profileFormState, {
    sync: true,
  });

  const proxyFields = profileFormState.fields;

  const { data, isLoading: dataLoading } = useQuery(AuthUserEditProfileDataNewDocument, {
    staleTime: 0,
    onSuccess({ data }) {
      const authUser = data.authUser;
      if (!authUser) return;

      //we only want to set the fields if they are empty that way we don't override any initial feed from local
      if (!proxyFields.displayName && authUser.displayName) {
        setField('displayName', authUser.displayName);
      }
      if (!proxyFields.bio) {
        setField('bio', authUser.description);
      }
      if (!proxyFields.delegateWallet) {
        setField('delegateWallet', authUser.delegateWalletAddress);
      }
      if (authUser.artist?.spotifyUrl && !proxyFields.spotifyUrl)
        setField('spotifyUrl', authUser.artist?.spotifyUrl);
      if (authUser.avatar?.key && !proxyFields.avatarUploadKey)
        setField('avatarUploadKey', authUser.avatar?.key);
      if (authUser.bannerImage?.key && !proxyFields.coverImageUploadKey)
        setField('coverImageUploadKey', authUser.bannerImage?.key);
      if (authUser.invitedByUser?.id && !proxyFields.invitedByUserId)
        setField('invitedByUserId', authUser.invitedByUser.id);
    },
    refetchOnWindowFocus: false,
  });

  const { locationSelect, locationValue } = useSingleLocationSelect({
    initialLocation: authUser?.location || null,
    placeholder: 'Find location',
    setIsKeyboardOpen: null,
    className: 'h-9 w-full',
  });

  const authUserData = data?.data.authUser;
  const existingDisplayName = authUserData?.displayName;
  const existingDescription = authUserData?.description;
  const existingDelegateWallet = authUserData?.delegateWalletAddress;
  const existingSpotifyUrl = authUserData?.artist?.spotifyUrl;
  const existingAvatarUploadKey = authUserData?.avatar?.key;
  const existingArtistCoverImageUploadKey = authUserData?.artist?.bannerImage?.key;
  const existingUserCoverImageUploadKey = authUserData?.bannerImage?.key;
  const existingLocation = authUser?.location;
  const existingInvitedByUserId = authUserData?.invitedByUser?.id;

  const setAvatarImage = async () => {
    if (!proxyFields.avatarUploadKey || proxyFields.avatarUploadKey === existingAvatarUploadKey)
      return;
    await fetchGQL(SetUserAvatarImageDocument, {
      variables: {
        image: {
          uploadKey: proxyFields.avatarUploadKey,
          mediaType: MediaType.AvatarImage,
        },
      },
    });
  };

  const setArtistCoverImage = async () => {
    if (
      !proxyFields.coverImageUploadKey ||
      proxyFields.coverImageUploadKey === existingArtistCoverImageUploadKey
    )
      return;
    await fetchGQL(UploadArtistBannerImageDocument, {
      variables: {
        bannerImage: {
          uploadKey: proxyFields.coverImageUploadKey,
          mediaType: MediaType.ArtistBannerImage,
        },
      },
    });
  };

  const setUserCoverImage = async () => {
    if (
      !proxyFields.coverImageUploadKey ||
      proxyFields.coverImageUploadKey === existingUserCoverImageUploadKey
    )
      return;
    await fetchGQL(UpsertUserBannerImageDocument, {
      variables: {
        bannerImage: {
          uploadKey: proxyFields.coverImageUploadKey,
          mediaType: MediaType.UserBannerImage,
        },
      },
    });
  };

  const setUserNickName = async () => {
    const newDisplayName = proxyFields.displayName ? deepTrim(proxyFields.displayName) : null;
    if (newDisplayName == null || newDisplayName === existingDisplayName) return;
    return fetchGQL(SetUserDisplayNameDocument, {
      variables: {
        displayName: newDisplayName,
      },
    });
  };

  const setDelegateAddress = async () => {
    if (proxyFields.delegateWallet === existingDelegateWallet) return;
    await fetchGQL(SetDelegateAddressDocument, {
      variables: {
        // We don't want to send empty string
        delegateAddress: proxyFields.delegateWallet?.trim() || null,
      },
    });
  };

  const setDescription = async () => {
    if (proxyFields.bio == null || proxyFields.bio === existingDescription) return;
    await fetchGQL(SetDescriptionDocument, {
      variables: {
        description: proxyFields.bio.trim(),
      },
    }).then(data => {
      setField('bio', data.data.updateDescription.description ?? '');
    });
  };

  const setArtistSpotifyUrl = async () => {
    if (proxyFields.spotifyUrl == null || proxyFields.spotifyUrl === existingSpotifyUrl) return;
    const { data } = await fetchGQL(SetArtistSpotifyUrlDocument, {
      variables: {
        spotifyUrl: proxyFields.spotifyUrl.trim() === '' ? null : proxyFields.spotifyUrl.trim(),
      },
    });

    if (data.updateSpotifyArtistUrl.__typename === 'MutationUpdateSpotifyArtistUrlSuccess') return;

    switch (data.updateSpotifyArtistUrl.__typename) {
      case 'ValidationError': {
        return {
          status: 'error',
          message: data.updateSpotifyArtistUrl.message,
        } as const;
      }
      default: {
        return {
          status: 'error',
        } as const;
      }
    }
  };

  const saveUserLocation = async () => {
    if ((locationValue?.label || null) === (existingLocation?.label || null)) return;
    await fetchGQL(SetUserLocationDocument, {
      variables: {
        location: locationValue
          ? {
              label: locationValue.label,
              placeId: locationValue.value.place_id,
            }
          : null,
      },
    });
  };

  const setInvitedByUserId = async () => {
    if (proxyFields.invitedByUserId === existingInvitedByUserId) return;
    await fetchGQL(SetInvitedByUserDocument, {
      variables: {
        invitedBy: proxyFields.invitedByUserId,
      },
    });
  };

  const onSave = useStableCallback(async () => {
    setIsSaving(true);
    return Promise.all([
      setAvatarImage(),
      setUserCoverImage(),
      setDelegateAddress(),
      setDescription(),
      setInvitedByUserId(),
      isArtist
        ? null
        : setUserNickName().then(data => {
            if (
              data &&
              data.data.setUserDisplayName.__typename !== 'MutationSetUserDisplayNameSuccess'
            ) {
              return {
                status: 'error',
              } as const;
            } else if (
              data?.data.setUserDisplayName.__typename === 'MutationSetUserDisplayNameSuccess'
            ) {
              setField('displayName', data.data.setUserDisplayName.data.displayName ?? '');
            }
            return;
          }),
      isArtist ? setArtistSpotifyUrl() : null,
      isArtist ? setArtistCoverImage() : null,
      saveUserLocation(),
    ])
      .then(data => {
        const resultsErrorMessagesList = getManyFromList(
          data,
          value => value?.status === 'error' && 'message' in value && value.message,
        );

        // TODO: https://linear.app/sound/issue/ENG-7880/build-error-modal-over-big-error-toast-for-edit-profile-multiple-error
        if (data.some(d => d?.status === 'error')) {
          const customErrorMessage = resultsErrorMessagesList.join('\n\n');

          if (customErrorMessage) {
            openToast({
              alignment: 'right',
              text: customErrorMessage,
              variant: 'error',
            });
          }

          if (data.length !== resultsErrorMessagesList.length) {
            openToast({
              alignment: 'right',
              text: 'Not all changes were saved. Please try again.',
              variant: 'error',
            });
          }

          return;
        }
        openToast({
          alignment: 'right',
          text: 'Your profile has been successfully updated',
          variant: 'success',
        });
        EditProfileFormPersistence.clear();
      })
      .catch(error => {
        // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
        logError({
          error,
          toast: 'An error occurred while saving your changes. Please try again.',
          level: 'error',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  });

  const fieldsLoading = Object.values(formLoading).some(loading => loading);
  const enableSave = !Object.values(errors).some(error => error !== null) && !fieldsLoading;

  return {
    onSave,
    loading: isSaving || dataLoading || fieldsLoading,
    fields: snapshotFields,
    errors,
    setField,
    setError,
    clearFields,
    clearErrors,
    locationSelect,
    clearErrorField,
    enableSave,
    setLoadingField,
  };
};
